import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { fetchIssues, createSubscription, updateIssue } from '../services/dataService';
import { MessageSquare, Search, CheckCircle, Clock, XCircle, Edit, Save } from 'lucide-react';
import { PageHeader } from '../components/layout';
import { Button, Input, Select, Card, Badge, Modal, ModalFooter, Spinner, EmptyState } from '../components/ui';
import { cn } from '../utils/cn';

const BYPASS_AUTH = import.meta.env.VITE_BYPASS_AUTH === 'true';
const STATUS_OPTIONS = ['Open', 'Answered', 'Closed'];

function ClarificationsManagement() {
  const { user, isAuthenticated, isLoading: authLoading } = BYPASS_AUTH
    ? { user: { email: 'mgmt@example.com' }, isAuthenticated: true, isLoading: false }
    : useAuth();

  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);  // Start true to show spinner on initial load
  const [apiError, setApiError] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState(['Open']);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [answerDraft, setAnswerDraft] = useState({ notes: '', status: '' });
  const [isSavingAnswer, setIsSavingAnswer] = useState(false);
  const [answerError, setAnswerError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

  useEffect(() => {
    // Wait for auth to settle before firing a request without a bearer
    if (authLoading) {
      return;
    }

    // Auth done but not authenticated - stop loading, don't fetch
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    let cleanup = null;

    (async () => {
      setIsLoading(true);
      setApiError(false);

      try {
        const fetched = await fetchIssues(['clarifications']);

        if (fetched === null) {
          setApiError(true);
        } else {
          setQuestions(fetched);
          cleanup = createSubscription(
            'swissues',
            (newData) => setQuestions(newData),
            () => fetchIssues(['clarifications'])
          );
        }
      } catch (err) {
        console.error('[ClarificationsManagement] API load error:', err);
        setApiError(true);
      } finally {
        setIsLoading(false);
      }
    })();

    return () => {
      if (cleanup) cleanup();
    };
  }, [authLoading, isAuthenticated]);

  const filteredQuestions = useMemo(() => {
    let filtered = questions;

    if (statusFilter.length > 0) {
      filtered = filtered.filter(q => statusFilter.includes(q.resolutionStatus || 'Open'));
    }

    if (searchInput.trim()) {
      const q = searchInput.toLowerCase();
      filtered = filtered.filter(item => {
        const created = item.createdAt ? new Date(item.createdAt).toLocaleString().toLowerCase() : '';
        const category = item.issueType || '';
        return (
          (item.comment || '').toLowerCase().includes(q) ||
          (item.resolutionNotes || '').toLowerCase().includes(q) ||
          (item.retailer || '').toLowerCase().includes(q) ||
          (item.level || '').toLowerCase().includes(q) ||
          (item.userEmail || '').toLowerCase().includes(q) ||
          category.toLowerCase().includes(q) ||
          created.includes(q)
        );
      });
    }

    return filtered;
  }, [questions, statusFilter, searchInput]);

  const sortedQuestions = useMemo(() => {
    if (!sortConfig.key) return filteredQuestions;

    return [...filteredQuestions].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'createdAt' || sortConfig.key === 'updatedAt') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue || '').toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredQuestions, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const openAnswerModal = (question) => {
    setSelectedQuestion(question);
    setAnswerDraft({
      notes: question.resolutionNotes || '',
      status: question.resolutionStatus || 'Open'
    });
    setAnswerError(null);
    setIsSavingAnswer(false);
    setShowAnswerModal(true);
  };

  const saveAnswer = async () => {
    if (isSavingAnswer || !selectedQuestion) return;

    setIsSavingAnswer(true);
    setAnswerError(null);

    const { error } = await updateIssue(selectedQuestion.id, {
      resolution_notes: answerDraft.notes,
      resolution_status: answerDraft.status,
    });

    if (error) {
      setAnswerError('Could not save answer. Please try again.');
      setIsSavingAnswer(false);
      return;
    }

    setQuestions(prev => prev.map(q =>
      q.id === selectedQuestion.id
        ? { ...q, resolutionNotes: answerDraft.notes, resolutionStatus: answerDraft.status, updatedAt: new Date().toISOString() }
        : q
    ));

    setIsSavingAnswer(false);
    setShowAnswerModal(false);
    setSelectedQuestion(null);
  };

  const toggleStatusFilter = (status) => {
    setStatusFilter(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const stats = useMemo(() => {
    const open = questions.filter(q => (q.resolutionStatus || 'Open') === 'Open').length;
    const answered = questions.filter(q => q.resolutionStatus === 'Answered').length;
    const closed = questions.filter(q => q.resolutionStatus === 'Closed').length;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const answeredToday = questions.filter(q => {
      if (q.resolutionStatus !== 'Answered' || !q.updatedAt) return false;
      const updated = new Date(q.updatedAt);
      return updated >= today;
    }).length;

    return { open, answered, closed, answeredToday, total: questions.length };
  }, [questions]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Answered':
        return (
          <Badge variant="success" className="gap-1">
            <CheckCircle size={12} /> Answered
          </Badge>
        );
      case 'Closed':
        return (
          <Badge variant="default" className="gap-1">
            <XCircle size={12} /> Closed
          </Badge>
        );
      default:
        return (
          <Badge variant="warning" className="gap-1">
            <Clock size={12} /> Open
          </Badge>
        );
    }
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Clarifications Management"
        description="Answer workforce questions and manage clarifications"
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card className="p-4 bg-gradient-to-br from-warning-50 to-warning-100 border-warning-200">
          <div className="text-xs font-semibold text-warning-700 uppercase mb-1">Open</div>
          <div className="text-2xl font-bold text-warning-900">{stats.open}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-success-50 to-success-100 border-success-200">
          <div className="text-xs font-semibold text-success-700 uppercase mb-1">Answered</div>
          <div className="text-2xl font-bold text-success-900">{stats.answered}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-neutral-50 to-neutral-100 border-neutral-200">
          <div className="text-xs font-semibold text-neutral-700 uppercase mb-1">Closed</div>
          <div className="text-2xl font-bold text-neutral-900">{stats.closed}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
          <div className="text-xs font-semibold text-primary-700 uppercase mb-1">Answered Today</div>
          <div className="text-2xl font-bold text-primary-900">{stats.answeredToday}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="text-xs font-semibold text-purple-700 uppercase mb-1">Total</div>
          <div className="text-2xl font-bold text-purple-900">{stats.total}</div>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="p-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1 max-w-xl">
            <Input
              placeholder="Search questions, answers, users..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              leadingIcon={<Search size={18} />}
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-neutral-600 font-medium">Filter:</span>
            {STATUS_OPTIONS.map(status => (
              <Button
                key={status}
                size="sm"
                variant={statusFilter.includes(status) ? 'primary' : 'secondary'}
                onClick={() => toggleStatusFilter(status)}
                className={cn(
                  statusFilter.includes(status) && status === 'Open' && 'bg-warning-600 hover:bg-warning-700 border-warning-600',
                  statusFilter.includes(status) && status === 'Answered' && 'bg-success-600 hover:bg-success-700 border-success-600',
                  statusFilter.includes(status) && status === 'Closed' && 'bg-neutral-600 hover:bg-neutral-700 border-neutral-600'
                )}
              >
                {status}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* Questions Table */}
      {isLoading ? (
        <Card className="flex items-center justify-center h-64">
          <Spinner size="lg" label="Loading clarifications..." />
        </Card>
      ) : apiError ? (
        <Card className="flex items-center justify-center h-64 bg-error-50 border-error-200">
          <p className="text-error-700 font-medium">Could not load clarifications from the API.</p>
        </Card>
      ) : sortedQuestions.length === 0 ? (
        <Card>
          <EmptyState
            icon={<MessageSquare />}
            title={searchInput || statusFilter.length < STATUS_OPTIONS.length ? 'No questions match your filters' : 'No clarifications found'}
            description={searchInput || statusFilter.length < STATUS_OPTIONS.length ? 'Try adjusting your search or filters' : 'Questions will appear here when users submit them'}
          />
        </Card>
      ) : (
        <Card noPadding className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th
                    onClick={() => handleSort('resolutionStatus')}
                    className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600 cursor-pointer hover:bg-neutral-100"
                  >
                    Status {sortConfig.key === 'resolutionStatus' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('createdAt')}
                    className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600 cursor-pointer hover:bg-neutral-100"
                  >
                    Date {sortConfig.key === 'createdAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('userEmail')}
                    className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600 cursor-pointer hover:bg-neutral-100"
                  >
                    Asker {sortConfig.key === 'userEmail' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                    Question
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                    Details
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {sortedQuestions.map((question) => {
                  const status = question.resolutionStatus || 'Open';
                  const category = question.issueType || 'General';

                  return (
                    <tr key={question.id} className="hover:bg-neutral-50">
                      <td className="px-4 py-3 text-sm text-neutral-700 whitespace-nowrap">
                        {getStatusBadge(status)}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700 whitespace-nowrap">
                        {formatRelativeTime(question.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700">
                        {question.userEmail || 'Unknown'}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700 max-w-md">
                        <div className="line-clamp-2">{question.comment}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700">
                        <div className="flex flex-col gap-1">
                          {question.retailer && (
                            <Badge variant="primary">{question.retailer}</Badge>
                          )}
                          {question.level && (
                            <Badge variant="primary">{question.level}</Badge>
                          )}
                          <Badge>{category}</Badge>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700 whitespace-nowrap">
                        <Button
                          size="sm"
                          onClick={() => openAnswerModal(question)}
                          leftIcon={<Edit size={14} />}
                        >
                          {status === 'Open' ? 'Answer' : 'View'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Answer Modal */}
      <Modal
        open={showAnswerModal}
        onClose={() => {
          if (!isSavingAnswer) {
            setShowAnswerModal(false);
            setSelectedQuestion(null);
          }
        }}
        title={answerDraft.status === 'Open' ? 'Answer Question' : 'View/Edit Clarification'}
        size="lg"
      >
        {answerError && (
          <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg">
            <p className="text-sm font-medium text-error-700">{answerError}</p>
          </div>
        )}

        {selectedQuestion && (
          <>
            {/* Question Details (Read-Only) */}
            <div className="mb-6 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold text-neutral-500 uppercase">Question Details</span>
                {getStatusBadge(selectedQuestion.resolutionStatus || 'Open')}
              </div>

              <p className="text-neutral-800 text-base mb-3 leading-relaxed">{selectedQuestion.comment}</p>

              <div className="flex items-center gap-4 text-xs text-neutral-600">
                <span>Asked by: <strong className="text-neutral-700">{selectedQuestion.userEmail || 'Unknown'}</strong></span>
                <span>Date: <strong className="text-neutral-700">{selectedQuestion.createdAt ? new Date(selectedQuestion.createdAt).toLocaleString() : 'N/A'}</strong></span>
              </div>

              {(selectedQuestion.retailer || selectedQuestion.level) && (
                <div className="flex items-center gap-2 mt-3">
                  {selectedQuestion.retailer && (
                    <Badge variant="primary">{selectedQuestion.retailer}</Badge>
                  )}
                  {selectedQuestion.level && (
                    <Badge variant="primary">{selectedQuestion.level}</Badge>
                  )}
                </div>
              )}
            </div>

            {/* Answer Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-neutral-500 uppercase mb-1">
                  Your Answer
                </label>
                <textarea
                  value={answerDraft.notes}
                  onChange={(e) => setAnswerDraft(prev => ({ ...prev, notes: e.target.value }))}
                  rows={5}
                  placeholder="Type your answer here..."
                  disabled={isSavingAnswer}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60"
                />
              </div>

              <Select
                label="Status"
                value={answerDraft.status}
                onChange={(e) => setAnswerDraft(prev => ({ ...prev, status: e.target.value }))}
                disabled={isSavingAnswer}
                options={STATUS_OPTIONS.map(status => ({ value: status, label: status }))}
              />
            </div>
          </>
        )}

        <ModalFooter>
          <Button
            variant="ghost"
            onClick={() => {
              if (!isSavingAnswer) {
                setShowAnswerModal(false);
                setSelectedQuestion(null);
              }
            }}
            disabled={isSavingAnswer}
          >
            Cancel
          </Button>
          <Button
            onClick={saveAnswer}
            loading={isSavingAnswer}
            leftIcon={<Save size={16} />}
          >
            Save Answer
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default ClarificationsManagement;
